<!--
  QuickCreateModal.vue

  Modal for creating person, organization, or deal records inline. Collects
  a curated set of disambiguation fields (beyond name/email) so enrichment
  has enough context to match the right entity. Opened either from the
  command palette or from quick-add UI (e.g. meeting attendees).
-->
<template>
  <ModalContainer :show="show" @close="close">
    <div class="quick-create-modal" style="width: 460px">
      <h2 class="text-4 mb-4">New {{ typeLabel }}</h2>

      <!-- Type + Schema selectors — schema is the type's subtype (e.g. "Venture Fund" for organization) -->
      <div class="field quick-create-type-row">
        <div class="quick-create-type-col">
          <label class="label is-small">Type</label>
          <TypeSelect
            v-model="form.type"
            size="small"
            exclude-list
            @update:model-value="handleTypeChange"
          />
        </div>
        <div class="quick-create-schema-col">
          <label class="label is-small">Subtype</label>
          <SchemaSelect
            v-model="form.schemaId"
            :record-type="form.type"
            size="small"
            none-label="None"
          />
        </div>
      </div>

      <!-- Name -->
      <div class="field">
        <label class="label is-small">Name <span class="text-danger">*</span></label>
        <input
          ref="nameInput"
          v-model="form.name"
          class="input"
          type="text"
          :placeholder="namePlaceholder"
          @keydown.enter.prevent="submit"
        />
      </div>

      <!-- Slug — only for generic types (person/org/deal auto-derive it) -->
      <div v-if="!isIntelligentType" class="field">
        <label class="label is-small">Slug <span class="text-muted">(optional)</span></label>
        <input
          v-model="form.slug"
          class="input"
          type="text"
          :maxlength="SLUG_MAX_LENGTH"
          :placeholder="slugPlaceholder"
          @keydown.enter.prevent="submit"
        />
      </div>

      <!-- Person-only fields -->
      <template v-if="form.type === 'person'">
        <div class="field">
          <label class="label is-small">
            Email <span class="text-danger">*</span>
            <span v-if="enrichingPerson" class="text-muted text-7 ml-2">
              <font-awesome-icon icon="spinner" spin />
              Enriching via Harmonic…
            </span>
          </label>
          <input
            v-model="form.email"
            class="input"
            type="email"
            placeholder="email@example.com"
            @blur="handleEmailBlur"
            @keydown.enter.prevent="submit"
          />
        </div>
      </template>

      <!-- Organization picker — shared by person (optional) and deal (required) -->
      <template v-if="form.type === 'person' || form.type === 'deal'">
        <div class="field">
          <label class="label is-small">
            Organization
            <span v-if="form.type === 'deal'" class="text-danger">*</span>
            <span v-else class="text-muted">(optional)</span>
          </label>
          <div v-click-outside="closeOrgDropdown" class="org-autocomplete">
            <div class="org-input-wrapper">
              <input
                v-model="form.organizationName"
                class="input"
                type="text"
                placeholder="Search organizations in Auger..."
                @input="handleOrgInput"
                @focus="handleOrgFocus"
                @keydown.esc="closeOrgDropdown"
                @keydown.enter.prevent="onOrgEnter"
              />
              <button
                v-if="form.organizationId"
                type="button"
                class="org-clear-btn"
                title="Clear selection"
                @click="clearOrg"
              >
                &times;
              </button>
            </div>

            <ul v-if="orgDropdownOpen" class="org-results">
              <li
                v-for="result in orgResults"
                :key="result.id"
                class="org-result-item"
                @mousedown.prevent="handleOrgSelect(result)"
              >
                {{ result.name }}
              </li>
              <li
                v-if="showCreateOrgOption"
                class="org-result-item is-create"
                @mousedown.prevent="createAndSelectOrg"
              >
                <font-awesome-icon icon="plus" class="mr-2" />
                Create new organization: "{{ trimmedOrgQuery }}"
              </li>
              <li
                v-if="!orgResults.length && !showCreateOrgOption && trimmedOrgQuery.length >= 2"
                class="org-result-item is-muted"
              >
                Keep typing to search...
              </li>
            </ul>
          </div>
        </div>
      </template>

      <!-- Person-only fields (continued) -->
      <template v-if="form.type === 'person'">
        <div class="field">
          <label class="label is-small">Phone <span class="text-muted">(optional)</span></label>
          <input
            v-model="form.phone"
            class="input"
            type="tel"
            placeholder="+1 555 000 0000"
            @keydown.enter.prevent="submit"
          />
        </div>

        <div class="field">
          <label class="label is-small">
            Role / Title <span class="text-muted">(optional)</span>
          </label>
          <input
            v-model="form.role"
            class="input"
            type="text"
            placeholder="e.g. CTO, Head of Eng"
            @keydown.enter.prevent="submit"
          />
        </div>

        <div class="field">
          <label class="label is-small">LinkedIn <span class="text-muted">(optional)</span></label>
          <input
            v-model="form.linkedin"
            class="input"
            type="url"
            placeholder="https://linkedin.com/in/..."
            @keydown.enter.prevent="submit"
          />
        </div>
      </template>

      <!-- Organization-only fields -->
      <template v-else-if="form.type === 'organization'">
        <div class="field">
          <label class="label is-small">Website <span class="text-muted">(optional)</span></label>
          <input
            v-model="form.website"
            class="input"
            type="url"
            placeholder="https://example.com"
            @keydown.enter.prevent="submit"
          />
        </div>

        <div class="field">
          <label class="label is-small">LinkedIn <span class="text-muted">(optional)</span></label>
          <input
            v-model="form.linkedin"
            class="input"
            type="url"
            placeholder="https://linkedin.com/company/..."
            @keydown.enter.prevent="submit"
          />
        </div>

        <div class="field">
          <label class="label is-small">
            Description <span class="text-muted">(optional)</span>
          </label>
          <textarea
            v-model="form.description"
            class="textarea"
            rows="2"
            placeholder="Short description for context"
          />
        </div>
      </template>

      <!-- Deal-only fields -->
      <template v-else-if="form.type === 'deal'">
        <div class="field">
          <label class="label is-small">
            Description <span class="text-muted">(optional)</span>
          </label>
          <textarea
            v-model="form.description"
            class="textarea"
            rows="2"
            placeholder="Short description for context"
          />
        </div>
      </template>

      <!-- Actions -->
      <div class="field is-grouped justify-end mt-5">
        <div class="control">
          <button class="btn btn-sm" @click="close">Cancel</button>
        </div>
        <div class="control">
          <button
            class="btn btn-sm btn-primary"
            :disabled="!canSubmit || submitting"
            @click="submit"
          >
            {{ submitting ? "Creating..." : `Create ${typeLabel}` }}
          </button>
        </div>
      </div>
    </div>
  </ModalContainer>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue"
import { toast } from "vue-sonner"
import ModalContainer from "@auger/ui/components/ModalContainer.vue"
import { SLUG_MAX_LENGTH } from "@auger/ui/utils/string"
import SchemaSelect from "@/components/selects/SchemaSelect.vue"
import TypeSelect from "@/components/selects/TypeSelect.vue"
import { useQuickCreate } from "@/composables/useQuickCreate"
import { client } from "@/init/apollo"

// All state + business logic (enrichment, org search/match, create
// orchestration) lives in the composable. This component is a form binding.
const {
  show,
  form,
  submitting,
  enrichingPerson,
  isIntelligentType,
  typeLabel,
  namePlaceholder,
  slugPlaceholder,
  canSubmit,
  orgResults,
  orgDropdownOpen,
  trimmedOrgQuery,
  showCreateOrgOption,
  handleTypeChange,
  handleEmailBlur,
  handleOrgInput,
  handleOrgFocus,
  handleOrgSelect,
  clearOrg,
  closeOrgDropdown,
  createAndSelectOrg,
  onOrgEnter,
  submit,
  close,
} = useQuickCreate({ client, toast })

const nameInput = ref<HTMLInputElement | null>(null)

// Focus the name input when the modal opens (DOM-only concern; the form
// itself is reset by the composable's open()).
watch(show, (isOpen) => {
  if (isOpen) nextTick(() => nameInput.value?.focus())
})
</script>

<style>
.quick-create-modal {
  .field {
    margin-bottom: 0.75rem;
  }

  .quick-create-type-row {
    display: flex;
    gap: 0.5rem;
  }

  .quick-create-type-col,
  .quick-create-schema-col {
    flex: 1;
  }

  .label {
    font-weight: 600;
    margin-bottom: 0.25rem;
  }

  .org-autocomplete {
    position: relative;
    width: 100%;
  }

  .org-input-wrapper {
    position: relative;
    width: 100%;
  }

  .org-clear-btn {
    position: absolute;
    top: 50%;
    right: 0.5rem;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    font-size: 1.1rem;
    line-height: 1;
    color: var(--auger-text-weak);
    cursor: pointer;
    padding: 0 0.25rem;

    &:hover {
      color: var(--auger-text);
    }
  }

  .org-results {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 2px 0 0 0;
    padding: 4px 0;
    list-style: none;
    background: var(--auger-bg);
    border: 1px solid var(--auger-border);
    border-radius: var(--auger-radius);
    box-shadow: var(--auger-shadow-3);
    max-height: 14rem;
    overflow-y: auto;
    z-index: 100;
  }

  .org-result-item {
    padding: 0.375rem 0.75rem;
    cursor: pointer;
    font-size: var(--auger-size-6);

    &:hover {
      background: var(--auger-bg-muted);
    }

    &.is-create {
      color: var(--auger-link-text);
      font-weight: 500;
      border-top: 1px solid var(--auger-border-light);
    }

    &.is-muted {
      color: var(--auger-text-weak);
      cursor: default;

      &:hover {
        background: transparent;
      }
    }
  }
}
</style>
